import React, { useCallback, useEffect, useMemo, useState } from "react";
import { visuallyHidden } from "@mui/utils";
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";

import notify from "../../Notification/helper";
import MissingProgressHeader from "./MissingProgressHeader";
import ProductOverridesMissingSelectedModal from "./ProductOverridesMissingSelectedModal";
import MissingReportsToolbar, {
  PRODUCT_OVERRIDES_MISSING_SORT_OPTIONS,
} from "./MissingReportsToolbar";
import MissingStatusChip from "./MissingStatusChip";
import {
  fetchMissingProgress,
  fetchMissingRows,
  fetchTaskRunDetails,
  getMissingCsvUrl,
  getOverrideResolveGatewayRequestPreview,
  lookupGatewayBeefProductIdByRefId,
  lookupGatewayOverrideByName,
  resolveGatewayRetailerContextForMissingRow,
  resolveOverrideMissingRecord,
  updateMissingStatus,
} from "../../../services/missingReports";

const COLLECTION = "beef_overrides_missing";

const SORT_UI_BY_VALUE = {
  created_desc: { id: "date_created", direction: "desc" },
  created_asc: { id: "date_created", direction: "asc" },
  override_name_asc: { id: "override_name", direction: "asc" },
  override_name_desc: { id: "override_name", direction: "desc" },
  retailer_asc: { id: "retailer", direction: "asc" },
  retailer_desc: { id: "retailer", direction: "desc" },
};

const SORT_VALUE_BY_COLUMN = {
  date_created: { asc: "created_asc", desc: "created_desc" },
  override_name: { asc: "override_name_asc", desc: "override_name_desc" },
  retailer: { asc: "retailer_asc", desc: "retailer_desc" },
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const SortableHeaderCell = ({ columnId, label, orderBy, order, onSort }) => (
  <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === columnId ? order : false}>
    <TableSortLabel
      active={orderBy === columnId}
      direction={orderBy === columnId ? order : "asc"}
      onClick={() => onSort(columnId)}
    >
      {label}
      {orderBy === columnId ? (
        <Box component="span" sx={visuallyHidden}>
          {order === "desc" ? "sorted descending" : "sorted ascending"}
        </Box>
      ) : null}
    </TableSortLabel>
  </TableCell>
);

const ProductOverridesMissingReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [status, setStatus] = useState("pending");
  const [retailerFilter, setRetailerFilter] = useState({ id: "", label: "" });
  const [includeMissingUnknownRetailers, setIncludeMissingUnknownRetailers] = useState(false);
  const [sortBy, setSortBy] = useState("created_desc");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [progress, setProgress] = useState({ total: 0, resolved: 0, remaining: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [taskRun, setTaskRun] = useState(null);
  const [loadingTaskRun, setLoadingTaskRun] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [retailerId, setRetailerId] = useState("");
  const [retailerName, setRetailerName] = useState("");
  const [retailerMode, setRetailerMode] = useState("lookup");
  const [suggestedRetailerId, setSuggestedRetailerId] = useState("");
  const [suggestedRetailerName, setSuggestedRetailerName] = useState("");
  const [retailerResolutionSource, setRetailerResolutionSource] = useState("none");
  const [hasRetailerRefId, setHasRetailerRefId] = useState(false);
  const [exactRetailerMatchFound, setExactRetailerMatchFound] = useState(false);
  const [beefProductId, setBeefProductId] = useState("");
  const [overrideName, setOverrideName] = useState("");
  const [gatewayOverrideStatus, setGatewayOverrideStatus] = useState({
    checking: false,
    exists: false,
    id: "",
  });

  const csvUrl = useMemo(() => getMissingCsvUrl("overrides"), []);
  const resolveRequestPreview = useMemo(() => {
    if (!selected) {
      return null;
    }

    return getOverrideResolveGatewayRequestPreview(selected, {
      retailerId,
      retailerName,
      beefProductId,
      overrideName,
      createRetailerIfMissing: false,
    });
  }, [selected, retailerId, retailerName, beefProductId, overrideName]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [rowsRes, progressRes] = await Promise.all([
        fetchMissingRows(COLLECTION, {
          page: page + 1,
          limit: rowsPerPage,
          status,
          retailerId: retailerFilter.id,
          includeMissingUnknownRetailers,
          sortBy,
        }),
        fetchMissingProgress(COLLECTION),
      ]);
      setRows(rowsRes.rows);
      setTotal(rowsRes.total);
      setProgress(progressRes);
      return rowsRes.rows || [];
    } catch (e) {
      setError(e.message || "Failed to load product override missing data.");
      return [];
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, status, retailerFilter.id, includeMissingUnknownRetailers, sortBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let active = true;
    async function loadModalContext() {
      if (!selected) {
        return;
      }
      setLoadingTaskRun(true);
      setTaskRun(null);
      try {
        const [taskRunRes, retailerContext, guessedBeefByRef] = await Promise.all([
          fetchTaskRunDetails(selected.task_run_id),
          resolveGatewayRetailerContextForMissingRow({
            retailerRefId: selected.retailer_ref_id || "",
            rawRetailerName: selected.retailer || "",
          }),
          lookupGatewayBeefProductIdByRefId(selected.ref_id || ""),
        ]);
        if (!active) {
          return;
        }
        setTaskRun(taskRunRes);
        setRetailerId(retailerContext?.retailerId || "");
        setSuggestedRetailerId(retailerContext?.retailerId || "");
        setSuggestedRetailerName(retailerContext?.retailerName || "");
        setRetailerName(retailerContext?.retailerName || selected.retailer || "");
        setRetailerResolutionSource(retailerContext?.source || "none");
        setHasRetailerRefId(Boolean(retailerContext?.hasRetailerRefId));
        setExactRetailerMatchFound(Boolean(retailerContext?.exactNameMatchFound));
        setRetailerMode("lookup");
        setBeefProductId(guessedBeefByRef || "");
        setOverrideName(selected.override_name || "");
      } catch (e) {
        if (!active) {
          return;
        }
        setError(e.message || "Failed to load task run details.");
      } finally {
        if (active) {
          setLoadingTaskRun(false);
        }
      }
    }
    loadModalContext();
    return () => {
      active = false;
    };
  }, [selected]);

  useEffect(() => {
    let active = true;

    async function checkGatewayOverride() {
      const sourceName = selected?.override_name || "";
      if (!selected || !sourceName.trim()) {
        setGatewayOverrideStatus({ checking: false, exists: false, id: "" });
        return;
      }

      setGatewayOverrideStatus((current) => ({
        checking: true,
        exists: current.exists,
        id: current.id,
      }));

      try {
        const existingOverride = await lookupGatewayOverrideByName({
          overrideName: sourceName,
          retailerId,
          beefProductId,
        });

        if (!active) {
          return;
        }

        setGatewayOverrideStatus({
          checking: false,
          exists: Boolean(existingOverride?.id),
          id: existingOverride?.id || "",
        });
      } catch (e) {
        if (!active) {
          return;
        }
        setGatewayOverrideStatus({ checking: false, exists: false, id: "" });
      }
    }

    checkGatewayOverride();

    return () => {
      active = false;
    };
  }, [selected?.id, selected?.override_name, retailerId, beefProductId]);

  const handleStatusOnlyAction = async (nextStatus) => {
    if (!selected) {
      return;
    }
    setActionLoading(true);
    setError("");
    try {
      await updateMissingStatus(COLLECTION, selected.id, nextStatus);
      notify("success", `Record marked as ${nextStatus}.`);
      setSelected(null);
      await loadData();
    } catch (e) {
      setError(e.message || "Failed to update status.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selected) {
      return false;
    }
    setActionLoading(true);
    setError("");
    try {
      await resolveOverrideMissingRecord(selected, {
        retailerId,
        retailerName,
        beefProductId,
        overrideName,
        createRetailerIfMissing: false,
      });
      await updateMissingStatus(COLLECTION, selected.id, "resolved");
      notify("success", "Override inserted into Gateway and record resolved.");
      return true;
    } catch (e) {
      setError(e.message || "Failed to resolve missing override record.");
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const moveToNextPending = useCallback(
    async (currentId) => {
      const refreshedRows = await loadData();
      const safeRows = Array.isArray(refreshedRows) ? refreshedRows : [];
      const currentIndex = safeRows.findIndex((row) => row.id === currentId);
      const nextOnCurrentPage = safeRows
        .slice(currentIndex >= 0 ? currentIndex + 1 : 0)
        .find((row) => row.status === "pending");

      if (nextOnCurrentPage) {
        setSelected(nextOnCurrentPage);
        return;
      }

      let nextPageToCheck = page + 2;
      while (true) {
        const pageRes = await fetchMissingRows(COLLECTION, {
          page: nextPageToCheck,
          limit: rowsPerPage,
          status,
          retailerId: retailerFilter.id,
          includeMissingUnknownRetailers,
          sortBy,
        });
        const pageRows = pageRes?.rows || [];
        if (!pageRows.length) {
          break;
        }

        const nextInPage = pageRows.find((row) => row.status === "pending");
        if (nextInPage) {
          setPage(nextPageToCheck - 1);
          setRows(pageRows);
          setTotal(pageRes?.total ?? 0);
          setSelected(nextInPage);
          return;
        }

        const totalCount = Number(pageRes?.total ?? 0);
        const lastPage = Math.max(1, Math.ceil(totalCount / rowsPerPage));
        if (nextPageToCheck >= lastPage) {
          break;
        }
        nextPageToCheck += 1;
      }

      setSelected(null);
    },
    [loadData, page, rowsPerPage, status, retailerFilter.id, includeMissingUnknownRetailers, sortBy]
  );

  const handleResolveAndNext = async () => {
    if (!selected) {
      return;
    }

    const currentId = selected.id;
    const resolved = await handleResolve();
    if (!resolved) {
      return;
    }

    await moveToNextPending(currentId);
  };

  const handleSkip = async () => {
    if (!selected) {
      return;
    }
    await moveToNextPending(selected.id);
  };

  const retailerSelectionInvalid = !retailerId;
  const resolveDisabled = !beefProductId || !overrideName.trim() || retailerSelectionInvalid;
  const sortUi = SORT_UI_BY_VALUE[sortBy] || SORT_UI_BY_VALUE.created_desc;

  const handleHeaderSort = (columnId) => {
    const columnSort = SORT_VALUE_BY_COLUMN[columnId];
    if (!columnSort) {
      return;
    }
    const isAsc = sortUi.id === columnId && sortUi.direction === "asc";
    setPage(0);
    setSortBy(columnSort[isAsc ? "desc" : "asc"]);
  };

  return (
    <Box
      sx={{
        width: "100%",
        boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
        position: "relative",
        padding: "16px",
        backgroundColor: "#efeeee",
      }}
    >
      <MissingProgressHeader
        title="Product Overrides / Missing"
        description="Resolve missing product overrides and keep retailer naming complete in Gateway."
        progress={progress}
      />
      <Alert severity="warning" sx={{ mb: 2 }}>
        This missing report workflow is still under development. Validate all changes before finalizing.
      </Alert>

      <MissingReportsToolbar
        status={status}
        onStatusChange={(nextStatus) => {
          setPage(0);
          setStatus(nextStatus);
        }}
        retailerId={retailerFilter.id}
        retailerLabel={retailerFilter.label}
        onRetailerChange={(nextRetailer) => {
          setPage(0);
          setRetailerFilter(nextRetailer);
        }}
        includeMissingUnknownRetailers={includeMissingUnknownRetailers}
        onIncludeMissingUnknownRetailersChange={(nextChecked) => {
          setPage(0);
          setIncludeMissingUnknownRetailers(nextChecked);
        }}
        sortBy={sortBy}
        onSortChange={(nextSortBy) => {
          setPage(0);
          setSortBy(nextSortBy);
        }}
        sortOptions={PRODUCT_OVERRIDES_MISSING_SORT_OPTIONS}
        csvUrl={csvUrl}
      />

      <Table
        sx={{
          width: "100%",
          border: "1px solid #eaeaea",
          backgroundColor: "#fff",
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <SortableHeaderCell
              columnId="override_name"
              label="Override Name"
              orderBy={sortUi.id}
              order={sortUi.direction}
              onSort={handleHeaderSort}
            />
            <TableCell sx={{ fontWeight: 600 }}>Normalized Name</TableCell>
            <SortableHeaderCell
              columnId="retailer"
              label="Retailer"
              orderBy={sortUi.id}
              order={sortUi.direction}
              onSort={handleHeaderSort}
            />
            <SortableHeaderCell
              columnId="date_created"
              label="Created"
              orderBy={sortUi.id}
              order={sortUi.direction}
              onSort={handleHeaderSort}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {(rows || []).map((row) => (
            <TableRow
              key={row.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setSelected(row);
                setError("");
              }}
            >
              <TableCell>
                <MissingStatusChip status={row.status} />
              </TableCell>
              <TableCell>{row.override_name || "-"}</TableCell>
              <TableCell>{row.normalized_product_name || "-"}</TableCell>
              <TableCell>{row.retailer || "-"}</TableCell>
              <TableCell>{formatDate(row.date_created)}</TableCell>
            </TableRow>
          ))}
          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2">No missing product override records found.</Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      <ProductOverridesMissingSelectedModal
        selected={selected}
        taskRun={taskRun}
        actionLoading={actionLoading}
        error={error}
        loadingTaskRun={loadingTaskRun}
        onClose={() => setSelected(null)}
        onResolve={handleResolveAndNext}
        onSkip={handleSkip}
        onReject={() => handleStatusOnlyAction("rejected")}
        resolveDisabled={resolveDisabled}
        skipDisabled={!selected}
        retailerId={retailerId}
        retailerName={retailerName}
        retailerMode={retailerMode}
        onRetailerModeChange={setRetailerMode}
        onRetailerNameChange={setRetailerName}
        onRetailerIdChange={setRetailerId}
        suggestedRetailerId={suggestedRetailerId}
        suggestedRetailerName={suggestedRetailerName}
        retailerResolutionSource={retailerResolutionSource}
        hasRetailerRefId={hasRetailerRefId}
        exactRetailerMatchFound={exactRetailerMatchFound}
        beefProductId={beefProductId}
        onBeefProductChange={setBeefProductId}
        overrideName={overrideName}
        onOverrideNameChange={setOverrideName}
        gatewayOverrideStatus={gatewayOverrideStatus}
        resolveRequestPreview={resolveRequestPreview}
      />
    </Box>
  );
};

export default ProductOverridesMissingReport;
